// src/styles/theme.js
// The MUI theme for the whole app, built from the design tokens. Components should rely on it rather than
// re-styling buttons, inputs, tables and so on by hand.

import { createTheme } from "@mui/material/styles";
import { colors, fontFamily, radius } from "./tokens";

const baseShadows = createTheme().shadows;
const shadows = [...baseShadows];
shadows[1] = "0 1px 2px rgba(23, 23, 23, 0.06)";
shadows[2] = "0 1px 3px rgba(23, 23, 23, 0.08), 0 1px 2px rgba(23, 23, 23, 0.04)";
shadows[3] = "0 4px 12px rgba(23, 23, 23, 0.06), 0 1px 3px rgba(23, 23, 23, 0.04)";

const theme = createTheme({
  palette: {
    primary: { main: colors.gold, dark: colors.goldDark, light: colors.beige, contrastText: "#FFFFFF" },
    secondary: { main: colors.ink, contrastText: "#FFFFFF" },
    success: { main: colors.success, light: colors.successBg },
    warning: { main: colors.warning, light: colors.warningBg },
    error: { main: colors.error, light: colors.errorBg },
    info: { main: colors.info, light: colors.infoBg },
    background: { default: colors.page, paper: colors.surface },
    text: { primary: colors.ink, secondary: colors.muted },
    divider: colors.border,
  },
  shadows,
  typography: {
    fontFamily,
    h1: { fontSize: "2.5rem", fontWeight: 700, lineHeight: 1.2 },
    h2: { fontSize: "2.125rem", fontWeight: 700, lineHeight: 1.2 }, // sign-in headings
    h3: { fontSize: "1.75rem", fontWeight: 600, lineHeight: 1.25 },
    h4: { fontSize: "1.625rem", fontWeight: 600, lineHeight: 1.3 }, // page titles
    h5: { fontSize: "1.3125rem", fontWeight: 600, lineHeight: 1.35 },
    h6: { fontSize: "1.125rem", fontWeight: 600, lineHeight: 1.4 }, // section titles
    subtitle1: { fontSize: "1rem", fontWeight: 600 }, // card titles
    subtitle2: { fontSize: "0.875rem", fontWeight: 600 },
    body1: { fontSize: "0.9375rem", lineHeight: 1.6 },
    body2: { fontSize: "0.875rem", lineHeight: 1.55, color: colors.text },
    caption: { fontSize: "0.8125rem", color: colors.muted },
    button: { fontWeight: 600, textTransform: "none", letterSpacing: 0 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: colors.page, color: colors.ink, WebkitFontSmoothing: "antialiased" },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: radius.control, minHeight: 40, paddingInline: 20, boxShadow: "none" },
        sizeSmall: { minHeight: 32, paddingInline: 14, fontSize: "0.8125rem" },
        sizeLarge: { minHeight: 52, paddingInline: 28, fontSize: "1rem" },
        containedPrimary: { "&:hover": { backgroundColor: colors.goldDark } },
        outlinedPrimary: {
          color: colors.ink,
          borderColor: colors.gold,
          "&:hover": { borderColor: colors.goldDark, backgroundColor: colors.beige },
        },
        textPrimary: { color: colors.goldText, "&:hover": { backgroundColor: colors.beige } },
      },
    },
    MuiIconButton: {
      styleOverrides: { root: { borderRadius: radius.control } },
    },
    MuiLink: {
      defaultProps: { underline: "hover" },
      styleOverrides: { root: { color: colors.goldText, fontWeight: 500 } },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: radius.control,
          backgroundColor: colors.surface,
          "& .MuiOutlinedInput-notchedOutline": { borderColor: colors.inputBorder },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: colors.muted },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: colors.gold, borderWidth: 2 },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: { root: { "&.Mui-focused": { color: colors.goldText } } },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: "none" },
        rounded: { borderRadius: radius.card },
        outlined: { borderColor: colors.border },
      },
    },
    MuiCard: {
      defaultProps: { variant: "outlined" },
      styleOverrides: { root: { borderRadius: radius.card } },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: { root: { backgroundColor: colors.black, color: "#FFFFFF" } },
    },
    MuiDialog: {
      styleOverrides: { paper: { borderRadius: radius.card } },
    },
    MuiDialogTitle: {
      styleOverrides: { root: { fontSize: "1.125rem", fontWeight: 600 } },
    },
    MuiDialogActions: {
      styleOverrides: { root: { padding: "12px 24px 20px", gap: 8 } },
    },
    MuiTableHead: {
      styleOverrides: { root: { backgroundColor: colors.beige } },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: colors.border },
        head: { color: colors.ink, fontWeight: 600, whiteSpace: "nowrap" },
      },
    },
    MuiTableRow: {
      styleOverrides: { root: { "&.MuiTableRow-hover:hover": { backgroundColor: "#FBF8F3" } } },
    },
    MuiChip: {
      styleOverrides: { root: { borderRadius: 8, fontWeight: 500 } },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: radius.control, alignItems: "center" },
        standardSuccess: { backgroundColor: colors.successBg, color: colors.success },
        standardWarning: { backgroundColor: colors.warningBg, color: colors.warning },
        standardError: { backgroundColor: colors.errorBg, color: colors.error },
        standardInfo: { backgroundColor: colors.infoBg, color: colors.info },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: radius.control,
          "&:hover": { backgroundColor: colors.beigeHover },
          "&.Mui-selected": {
            backgroundColor: colors.beige,
            color: colors.goldText,
            "& .MuiListItemIcon-root": { color: colors.goldText },
            "&:hover": { backgroundColor: colors.beigeHover },
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: { root: { color: colors.muted, minWidth: 36 } },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          "&.Mui-selected": { backgroundColor: colors.beige },
          "&.Mui-selected:hover": { backgroundColor: colors.beigeHover },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: { tooltip: { backgroundColor: colors.ink, fontSize: "0.75rem" } },
    },
    MuiAvatar: {
      styleOverrides: { colorDefault: { backgroundColor: colors.gold, color: "#FFFFFF" } },
    },
    MuiToggleButton: {
      styleOverrides: { root: { textTransform: "none" } },
    },
    MuiTab: {
      styleOverrides: { root: { textTransform: "none", fontWeight: 600 } },
    },
  },
});

export default theme;
